/**
 * Process-level status for live work:
 *  - GET …/engineering-intelligence/status — scheduler lease, idle/stale state,
 *    auto-trigger attempts and scheduler health for one mission run.
 *  - GET /platform/in-progress — live tasks, engineering requests and missions
 *    per accessible project.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient, type SchedulerHealth } from './api';
import { pollIntervalMs } from '../lib/queryPolling';

export interface MissionRunProcessStatus {
  run_id: string;
  state: string;
  created_at: string;
  updated_at: string;
  elapsed_seconds: number;
  idle_seconds: number;
  auto_resumable: boolean;
  /** Idle past the scheduler's stale threshold — due to be resumed. */
  stale: boolean;
  resume_eligible_at?: string;
  lease_holder?: string;
  lease_claimed_at?: string;
  lease_active: boolean;
}

export interface MissionTriggerStatus {
  id: string;
  project_id: string;
  change_request_id: string;
  status: 'pending' | 'running' | 'succeeded' | 'failed' | 'blocked' | string;
  attempt_count: number;
  max_attempts: number;
  last_error?: string;
  run_id?: string;
  next_attempt_at?: string;
  created_at: string;
  updated_at: string;
}

export interface EngineeringMissionStatus {
  generated_at: string;
  ei_enabled: boolean;
  lease_seconds: number;
  stale_after_sec: number;
  scheduler: SchedulerHealth;
  run: MissionRunProcessStatus | null;
  trigger: MissionTriggerStatus | null;
}

export interface MissionStatusTarget {
  incidentId?: string | null;
  crId?: string | null;
}

export function missionStatusEndpoint(projectId: string, target: MissionStatusTarget): string | null {
  if (!projectId) return null;
  if (target.crId) {
    return `/projects/${projectId}/change-requests/${encodeURIComponent(target.crId)}/engineering-intelligence/status`;
  }
  if (target.incidentId) {
    return `/projects/${projectId}/ams/incidents/${encodeURIComponent(target.incidentId)}/engineering-intelligence/status`;
  }
  return null;
}

export function useEngineeringMissionStatus(
  projectId: string,
  target: MissionStatusTarget,
  opts: { enabled?: boolean } = {},
) {
  const endpoint = missionStatusEndpoint(projectId, target);
  return useQuery({
    queryKey: ['projects', projectId, 'mission-status', target.crId ?? null, target.incidentId ?? null],
    queryFn: ({ signal }) => apiClient<EngineeringMissionStatus>(endpoint!, { signal }),
    enabled: Boolean(endpoint) && opts.enabled !== false,
    staleTime: 5_000,
    refetchInterval: () => pollIntervalMs(10_000),
  });
}

export type InProgressActivity = 'running' | 'waiting' | 'needs_you' | 'stalled';

export interface InProgressItem {
  kind: 'task' | 'request' | 'mission';
  id: string;
  project_id: string;
  title: string;
  status: string;
  activity: InProgressActivity | string;
  task_type?: string;
  run_id?: string;
  run_state?: string;
  phase?: string;
  iteration?: number;
  max_iterations?: number;
  request_id?: string;
  incident_id?: string;
  change_request_id?: string;
  error?: string;
  started_at: string;
  updated_at: string;
  lease_holder?: string;
  lease_claimed_at?: string;
}

export interface InProgressCounts {
  total: number;
  running: number;
  waiting: number;
  needs_you: number;
  stalled: number;
}

export interface InProgressProject {
  project_id: string;
  project_name?: string;
  counts: InProgressCounts;
  items: InProgressItem[];
  truncated?: boolean;
}

export interface InProgressWork {
  projects: InProgressProject[];
  totals: InProgressCounts;
  sources_unavailable: string[];
  task_stall_after_sec: number;
  run_stale_after_sec: number;
  generated_at: string;
}

export function useInProgressWork(projectId?: string) {
  const query = useQuery({
    queryKey: ['platform', 'in-progress', projectId ?? null],
    queryFn: () =>
      apiClient<InProgressWork>(
        projectId ? `/platform/in-progress?project_id=${encodeURIComponent(projectId)}` : '/platform/in-progress',
      ),
    staleTime: 15_000,
    refetchInterval: () => pollIntervalMs(30_000),
  });
  const byProject = useMemo(() => {
    const map = new Map<string, InProgressProject>();
    for (const p of query.data?.projects ?? []) map.set(p.project_id, p);
    return map;
  }, [query.data]);
  return { ...query, byProject };
}
